import React, { useEffect, useRef, useState } from "react";
import { LANG_NAMES, type CaptionLang } from "@hudhud/core";
import { Icon } from "./Icon.js";
import type { ClipFiles } from "../api.js";

/** A download URL that saves as `name` even when the browser would rather play it. */
export const downloadUrl = (url: string, name: string) => `${url}${url.includes("?") ? "&" : "?"}download=${encodeURIComponent(name)}`;

export interface DownloadItem { label: string; url: string; name: string; sub?: string }

/** Every file a clip has to offer: the English video, each language video, the music-free cut, the cover and the zip. */
export function downloadItems(videoId: string, clipId: string, files: ClipFiles | null | undefined): DownloadItem[] {
  if (!files) return [];
  const base = `${videoId}-${clipId}`;
  const items: DownloadItem[] = [];
  if (files.render) items.push({ label: "English video", url: files.render, name: `${base}.mp4`, sub: "MP4" });
  for (const [l, url] of Object.entries(files.renders ?? {})) if (url) items.push({ label: `${LANG_NAMES[l as CaptionLang] ?? l} video`, url, name: `${base}-${l}.mp4`, sub: "MP4" });
  if (files.renderNoMusic) items.push({ label: "Video without BG audio", url: files.renderNoMusic, name: `${base}-no-bg-audio.mp4`, sub: "add a nasheed on the platform" });
  if (files.cover) items.push({ label: "Cover image", url: files.cover, name: `${base}-cover.jpg`, sub: "JPG" });
  if (files.pack) items.push({ label: "Everything", url: files.pack, name: `${base}.zip`, sub: "videos, covers, captions, post texts" });
  return items;
}

/** A Download button with a menu of the clip's files; hidden when there is nothing to download yet. */
export const DownloadsMenu: React.FC<{ videoId: string; clipId: string; files: ClipFiles | null | undefined; compact?: boolean }> = ({ videoId, clipId, files, compact }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const items = downloadItems(videoId, clipId, files);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [open]);
  if (!items.length) return null;
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button className={`btn sm${compact ? "" : " soft"}`} onClick={() => setOpen(!open)} title="Download the finished videos and files"><Icon name="download" size={14} />{compact ? null : "Download"}{items.length > 1 ? <span className="muted" style={{ fontSize: 11, marginLeft: 2 }}>{items.length}</span> : null}</button>
      {open ? (
        <div className="card col" style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 40, minWidth: 250, padding: 6, gap: 2, boxShadow: "var(--shadow-2)" }}>
          {items.map((it) => (
            <a key={it.name} href={downloadUrl(it.url, it.name)} download={it.name} onClick={() => setOpen(false)} className="row" style={{ gap: 10, padding: "8px 10px", borderRadius: 8, color: "inherit", textDecoration: "none", alignItems: "center" }} onMouseEnter={(e) => (e.currentTarget.style.background = "var(--panel3)")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              <Icon name="download" size={14} />
              <span className="col" style={{ gap: 1, minWidth: 0 }}><span style={{ fontSize: 13, fontWeight: 700 }}>{it.label}</span>{it.sub ? <span className="muted" style={{ fontSize: 11 }}>{it.sub}</span> : null}</span>
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
};
